/**
 * Minimal EBML primitives — EBML is the container grammar behind WebM/Matroska.
 *
 * Only the pieces needed to rewrite a MediaRecorder segment header are implemented;
 * anything else is treated as opaque bytes by the callers in this folder.
 */

/** An unsigned integer encoded with a leading length marker. */
export interface VInt {
    /** Value with the length marker stripped. */
    value: number;
    /** How many bytes the encoding occupies. */
    length: number;
    /** `true` when every value bit is set — EBML's "size is not known yet" marker. */
    unknown: boolean;
}

const MAX_VINT_LENGTH = 8;

/** Number of bytes a vint occupies, derived from the position of the marker bit. */
const vintLength = (firstByte: number): number => {
    for (let length = 1; length <= MAX_VINT_LENGTH; length++) {
        if (firstByte & (0x80 >> (length - 1))) return length;
    }
    return 0;
};

export const readVInt = (bytes: Uint8Array, offset: number): VInt | null => {
    if (offset >= bytes.length) return null;

    const length = vintLength(bytes[offset]);
    if (length === 0 || offset + length > bytes.length) return null;

    let value = bytes[offset] & (0xff >> length);
    let unknown = value === 0xff >> length;

    for (let i = 1; i < length; i++) {
        value = value * 0x100 + bytes[offset + i];
        unknown = unknown && bytes[offset + i] === 0xff;
    }

    return { value, length, unknown };
};

/** Element IDs keep their length marker, so they are read as raw big-endian bytes. */
export const readId = (bytes: Uint8Array, offset: number): VInt | null => {
    if (offset >= bytes.length) return null;

    const length = vintLength(bytes[offset]);
    if (length === 0 || length > 4 || offset + length > bytes.length) return null;

    let value = 0;
    for (let i = 0; i < length; i++) value = value * 0x100 + bytes[offset + i];

    return { value, length, unknown: false };
};

export const writeId = (id: number): Uint8Array => {
    const length = id > 0xffffff ? 4 : id > 0xffff ? 3 : id > 0xff ? 2 : 1;
    const bytes = new Uint8Array(length);

    let rest = id;
    for (let i = length - 1; i >= 0; i--) {
        bytes[i] = rest % 0x100;
        rest = Math.floor(rest / 0x100);
    }

    return bytes;
};

export const writeVInt = (value: number): Uint8Array => {
    for (let length = 1; length <= MAX_VINT_LENGTH; length++) {
        // An all-ones payload is reserved for "unknown size", so it cannot carry a value.
        if (value >= Math.pow(2, 7 * length) - 1) continue;

        const bytes = new Uint8Array(length);
        let rest = value;
        for (let i = length - 1; i > 0; i--) {
            bytes[i] = rest % 0x100;
            rest = Math.floor(rest / 0x100);
        }
        bytes[0] = rest | (0x80 >> (length - 1));

        return bytes;
    }

    throw new RangeError(`${value} does not fit in an EBML variable-length integer.`);
};

export const readUInt = (bytes: Uint8Array, start: number, end: number): number => {
    let value = 0;
    for (let i = start; i < end; i++) value = value * 0x100 + bytes[i];
    return value;
};

export const writeFloat64 = (value: number): Uint8Array => {
    const bytes = new Uint8Array(8);
    new DataView(bytes.buffer).setFloat64(0, value);
    return bytes;
};

export const writeElement = (id: number, payload: Uint8Array): Uint8Array => {
    const idBytes = writeId(id);
    const sizeBytes = writeVInt(payload.length);

    const element = new Uint8Array(idBytes.length + sizeBytes.length + payload.length);
    element.set(idBytes, 0);
    element.set(sizeBytes, idBytes.length);
    element.set(payload, idBytes.length + sizeBytes.length);

    return element;
};

export const concatBytes = (parts: Uint8Array[]): Uint8Array => {
    const result = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));

    let offset = 0;
    for (const part of parts) {
        result.set(part, offset);
        offset += part.length;
    }

    return result;
};
